import React from 'react'
import { format } from 'date-fns'
import { formatDateOnly } from '../../utils/formatting'
import { SampleRequestDetail } from '../../models/samples'
import { FbLink } from '../FbUI/FbLink'
import { TextPill } from '../FbUI/TextPill'
import { LineItemDisplay } from './LineItemDisplay'

interface SampleRequestPreviewProps {
  order: SampleRequestDetail
  showHeader?: boolean
}

export function SampleRequestPreview({
  order,
  showHeader = true,
}: SampleRequestPreviewProps) {
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy hh:mm a')
    } catch {
      return dateString
    }
  }

  return (
    <div>
      {showHeader && (
        <>
          <div className="flex justify-between items-center mb-3">
            <div>
              <h2 className="mb-1">Order #{order.request_number}</h2>
              <div className="text-gray-500">
                Created {formatDateTime(order.created)}
              </div>
            </div>
            <TextPill
              backgroundColor={
                order.status === 'submitted' ? '#16A34A' : '#DC2626'
              }
              textColor="#FFFFFF"
              style={{ marginBottom: 2 }}
            >
              {order.status_display}
            </TextPill>
          </div>
          <hr />
        </>
      )}

      <h3 className="text-lg font-semibold mb-3 mt-6 first:mt-0 text-gray-800 border-b-2 border-gray-200 pb-2">
        Order Information
      </h3>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Date Needed:
        </div>
        <div className="text-gray-800 flex-1">
          {formatDateOnly(order.date_needed)}
        </div>
      </div>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Fulfillment Option:
        </div>
        <div className="text-gray-800 flex-1">
          {order.fulfillment_option_display}
        </div>
      </div>
      {order.distributor_name && (
        <div className="flex mb-2 text-[0.95rem]">
          <div className="font-semibold min-w-[180px] text-gray-700">
            Distributor:
          </div>
          <div className="text-gray-800 flex-1">{order.distributor_name}</div>
        </div>
      )}

      <h3 className="text-lg font-semibold mb-3 mt-6 first:mt-0 text-gray-800 border-b-2 border-gray-200 pb-2">
        Contact & Company
      </h3>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Company:
        </div>
        <div className="text-gray-800 flex-1">
          <FbLink to={`/company/${order.company_id}`}>
            {order.company_name}
          </FbLink>
        </div>
      </div>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Contact Name:
        </div>
        <div className="text-gray-800 flex-1">
          {order.contact_first_name} {order.contact_last_name}
        </div>
      </div>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Contact Phone:
        </div>
        <div className="text-gray-800 flex-1">{order.contact_phone || '-'}</div>
      </div>
      <div className="flex mb-2 text-[0.95rem]">
        <div className="font-semibold min-w-[180px] text-gray-700">
          Requested By:
        </div>
        <div className="text-gray-800 flex-1">
          {order.user_name} ({order.user_email})
        </div>
      </div>

      {order.fulfillment_option === 'ship_to_address' && (
        <>
          <h3 className="text-lg font-semibold mb-3 mt-6 first:mt-0 text-gray-800 border-b-2 border-gray-200 pb-2">
            Shipping Address
          </h3>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              Address Line 1:
            </div>
            <div className="text-gray-800 flex-1">
              {order.shipping_address_line1}
            </div>
          </div>
          {order.shipping_address_line2 && (
            <div className="flex mb-2 text-[0.95rem]">
              <div className="font-semibold min-w-[180px] text-gray-700">
                Address Line 2:
              </div>
              <div className="text-gray-800 flex-1">
                {order.shipping_address_line2}
              </div>
            </div>
          )}
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              City:
            </div>
            <div className="text-gray-800 flex-1">{order.shipping_city}</div>
          </div>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              State:
            </div>
            <div className="text-gray-800 flex-1">{order.shipping_state}</div>
          </div>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              ZIP Code:
            </div>
            <div className="text-gray-800 flex-1">{order.shipping_zip}</div>
          </div>
          <div className="flex mb-2 text-[0.95rem]">
            <div className="font-semibold min-w-[180px] text-gray-700">
              Country:
            </div>
            <div className="text-gray-800 flex-1">
              {order.shipping_country || 'US'}
            </div>
          </div>
        </>
      )}

      <h3 className="text-lg font-semibold mb-3 mt-6 first:mt-0 text-gray-800 border-b-2 border-gray-200 pb-2">
        Products
      </h3>
      {order.line_items && order.line_items.length > 0 ? (
        order.line_items.map((item) => (
          <LineItemDisplay key={item.id} item={item} />
        ))
      ) : (
        <div className="text-gray-500">No products selected</div>
      )}

      {order.special_instructions && (
        <>
          <h3 className="text-lg font-semibold mb-3 mt-6 first:mt-0 text-gray-800 border-b-2 border-gray-200 pb-2">
            Special Instructions
          </h3>
          <div className="p-3 bg-gray-50 rounded whitespace-pre-wrap">
            {order.special_instructions}
          </div>
        </>
      )}
    </div>
  )
}
